// Novos DNAs · Card de visita: SUGERIR → aguardar confirmação → CONFIRMAR → mensagem de confirmação
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, dd] = String(d).slice(0, 10).split("-");
  return `${dd}/${m}/${y}`;
};

const waLink = (phoneNorm, msg) => {
  const digits = String(phoneNorm || "").replace(/\D/g, "");
  const full = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg || "")}`;
};

export const VisitFlowCard = ({ prospect, onUpdated, styles: S }) => {
  const p = prospect;
  const scheduled = !!p.visit_scheduled_at;
  const waiting = p.status === "aguardando_confirmacao" || (!!p.suggestion_sent_at && !scheduled);

  const [date, setDate] = useState(p.suggested_visit_date || "");
  const [time, setTime] = useState(p.suggested_visit_time || "");
  const [msg, setMsg] = useState(p.suggestion_message || "");
  const [confMsg, setConfMsg] = useState(p.confirmation_message || "");
  const [busy, setBusy] = useState("");
  const [warn, setWarn] = useState("");
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");

  const currentDate = p.visit_date || p.suggested_visit_date || (p.visit_scheduled_at ? String(p.visit_scheduled_at).slice(0, 10) : "");
  const currentTime = p.visit_time || p.suggested_visit_time || (p.visit_scheduled_at ? String(p.visit_scheduled_at).slice(11, 16) : "");

  const startEdit = () => { setEditDate(currentDate); setEditTime(currentTime); setEditing(true); };

  const saveReschedule = async (override = false) => {
    if (!editDate || !editTime) { toast.error("Informe data e horário."); return; }
    setBusy("reschedule");
    try {
      const { data } = await axios.post(`${API}/prospects/${p.id}/visit/reschedule`,
        { visit_date: editDate, visit_time: editTime, override_conflict: override }, { headers: authHeaders() });
      setDate(editDate); setTime(editTime); setEditing(false);
      const nova = `Olá, ${(p.responsible_name || "").split(" ")[0]}! Ajustamos a visita de ${p.student_name || "seu filho(a)"} ao Novo DNA.\n📅 Nova data: ${fmtDate(editDate)}\n🕐 Novo horário: ${editTime}\nEsse novo horário funciona para vocês?`;
      setMsg(nova);
      setConfMsg("");
      onUpdated(data);
      toast.success("Visita reagendada · histórico registrado");
    } catch (e) {
      const detail = e?.response?.data?.detail || "erro inesperado";
      if (e?.response?.status === 409) {
        if (window.confirm(`${detail}\n\nDeseja salvar mesmo assim?`)) return saveReschedule(true);
      } else {
        toast.error(`Não foi possível reagendar: ${detail}`);
      }
    } finally { setBusy(""); }
  };

  const editBlock = (
    <div style={{ marginTop: 10, padding: 10, background: "#faf7fd", border: "1px solid #ede7f7", borderRadius: 10 }}
         data-testid="visit-edit-block">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8 }}>
        <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} style={S.input} data-testid="visit-edit-date" />
        <input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} style={S.input} data-testid="visit-edit-time" />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => saveReschedule(false)} disabled={busy === "reschedule"}
          style={{ ...S.btnAction("#7B3F98"), marginTop: 8, flex: 1 }} data-testid="visit-edit-save">
          {busy === "reschedule" ? "Salvando..." : "Salvar alteração"}
        </button>
        <button onClick={() => setEditing(false)} style={{ ...S.btnAction("#94a3b8"), marginTop: 8, flex: 1 }}
          data-testid="visit-edit-cancel">Cancelar</button>
      </div>
    </div>
  );

  const gen = async (intent) => {
    const d = scheduled ? (p.visit_date || String(p.visit_scheduled_at).slice(0, 10)) : date;
    const t = scheduled ? (p.visit_time || String(p.visit_scheduled_at).slice(11, 16)) : time;
    if (!d || !t) { toast.error("Informe data e horário."); return; }
    setBusy(intent);
    try {
      const { data } = await axios.post(`${API}/prospects/${p.id}/visit/message`,
        { visit_date: d, visit_time: t, intent }, { headers: authHeaders() });
      if (intent === "VISIT_CONFIRMATION") setConfMsg(data.message); else setMsg(data.message);
      setWarn(data.address_warning || "");
      if (data.source === "fallback") toast.info("Mensagem gerada localmente (IA indisponível).");
    } catch (e) {
      toast.error(`Não foi possível gerar a mensagem: ${e?.response?.data?.detail || "erro inesperado"}`);
    } finally { setBusy(""); }
  };

  const sendSuggestion = async () => {
    if (!msg.trim()) { toast.error("Gere ou escreva a mensagem antes de enviar."); return; }
    window.open(waLink(p.responsible_phone_norm || p.responsible_phone, msg), "_blank");
    try {
      const { data } = await axios.post(`${API}/prospects/${p.id}/visit/suggestion-sent`,
        { visit_date: date, visit_time: time, message: msg, channel: "whatsapp" }, { headers: authHeaders() });
      onUpdated(data);
      toast.success("Sugestão registrada · aguardando confirmação da família");
    } catch (e) {
      toast.error(`Não foi possível registrar a sugestão: ${e?.response?.data?.detail || "erro"}`);
    }
  };

  const confirm = async () => {
    const d = date || p.suggested_visit_date;
    const t = time || p.suggested_visit_time;
    if (!d || !t) { toast.error("Sem data/hora sugerida para confirmar."); return; }
    setBusy("confirm");
    try {
      const { data } = await axios.post(`${API}/prospects/${p.id}/visit/confirm`,
        { visit_date: d, visit_time: t }, { headers: authHeaders() });
      onUpdated(data);
      toast.success("Visita agendada!");
    } catch (e) {
      toast.error(`Não foi possível confirmar: ${e?.response?.data?.detail || "erro"}`);
    } finally { setBusy(""); }
  };

  const sendConfirmation = async (channel) => {
    if (!confMsg.trim()) { toast.error("Gere ou escreva a mensagem de confirmação."); return; }
    if (channel === "whatsapp") {
      window.open(waLink(p.responsible_phone_norm || p.responsible_phone, confMsg), "_blank");
    } else {
      window.open(`mailto:${p.responsible_email}?subject=${encodeURIComponent("Visita confirmada | Novo DNA")}&body=${encodeURIComponent(confMsg)}`, "_blank");
    }
    try {
      const { data } = await axios.post(`${API}/prospects/${p.id}/visit/confirmation-sent`,
        { message: confMsg, channel }, { headers: authHeaders() });
      onUpdated(data);
      toast.success("Confirmação registrada na timeline");
    } catch { /* já abriu o canal */ }
  };

  const btn = (color) => ({ ...S.btnAction(color), marginTop: 8 });

  return (
    <div style={S.actionCard} data-testid="novosdna-visit-card">
      {!scheduled ? (
        <>
          <div style={S.actionCardHead}>📅 Sugerir uma visita</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8 }}>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              style={S.input} data-testid="visit-date" />
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              style={S.input} data-testid="visit-time" />
          </div>
          <button onClick={() => gen("VISIT_SUGGESTION")} disabled={!date || !time || busy}
            style={{ ...btn("#7B3F98"), opacity: date && time && !busy ? 1 : 0.55 }} data-testid="visit-generate">
            {busy === "VISIT_SUGGESTION" ? "Gerando..." : "✨ Gerar sugestão"}
          </button>

          {msg && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#7B3F98", marginBottom: 4 }}>
                ✨ Sugestão para WhatsApp
              </div>
              <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={7}
                style={{ ...S.input, resize: "vertical", lineHeight: 1.5 }} data-testid="visit-message" />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => gen("VISIT_SUGGESTION")} disabled={busy}
                  style={{ ...btn("#8E52AB"), flex: 1 }} data-testid="visit-regenerate">🔄 Gerar outra</button>
                <button onClick={sendSuggestion} style={{ ...btn("#25D366"), flex: 1 }} data-testid="visit-send-whatsapp">
                  WhatsApp
                </button>
              </div>
            </div>
          )}

          {waiting && (
            <div style={{ marginTop: 12, padding: 10, background: "#fefce8", border: "1px solid #fde68a", borderRadius: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#a16207" }} data-testid="visit-waiting-badge">
                🟡 Aguardando confirmação
              </div>
              <div style={{ fontSize: 12, color: "#854d0e", marginTop: 2 }}>
                Sugerido: {fmtDate(p.suggested_visit_date)} às {p.suggested_visit_time}
              </div>
              {editing ? editBlock : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={confirm} disabled={busy === "confirm"} style={{ ...btn("#10b981"), flex: 1 }} data-testid="visit-confirm">
                    ✓ Confirmar agendamento
                  </button>
                  <button onClick={startEdit} style={{ ...btn("#7B3F98"), flex: 1 }} data-testid="visit-edit-open">
                    Editar data e horário
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div style={S.actionCardHead}>✓ Visita confirmada</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#10b981" }} data-testid="visit-confirmed-info">
            {fmtDate(p.visit_date || p.visit_scheduled_at)} • {p.visit_time || String(p.visit_scheduled_at).slice(11, 16)}
          </div>
          {editing ? editBlock : (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => gen("VISIT_CONFIRMATION")} disabled={busy}
                style={{ ...btn("#7B3F98"), flex: 1 }} data-testid="visit-generate-confirmation">
                {busy === "VISIT_CONFIRMATION" ? "Gerando..." : "✨ Gerar confirmação"}
              </button>
              <button onClick={startEdit} style={{ ...btn("#f97316"), flex: 1 }} data-testid="visit-edit-open">
                Editar data e horário
              </button>
            </div>
          )}
          {p.status === "reagendada" && msg && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#f97316", marginBottom: 4 }}>Novo horário para avisar a família</div>
              <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5}
                style={{ ...S.input, resize: "vertical", lineHeight: 1.5 }} data-testid="visit-reschedule-message" />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { window.open(waLink(p.responsible_phone_norm || p.responsible_phone, msg), "_blank"); }}
                  style={{ ...btn("#25D366"), flex: 1 }} data-testid="visit-send-new-time-whatsapp">
                  Enviar novo horário pelo WhatsApp
                </button>
                <button onClick={() => sendConfirmation("email")} style={{ ...btn("#0ea5e9"), flex: 1 }}
                  data-testid="visit-resend-confirmation-email">Reenviar confirmação</button>
              </div>
            </div>
          )}
          {warn && <div style={{ marginTop: 6, fontSize: 11, color: "#b45309" }}>{warn}</div>}
          {confMsg && (
            <div style={{ marginTop: 12 }}>
              <textarea value={confMsg} onChange={(e) => setConfMsg(e.target.value)} rows={7}
                style={{ ...S.input, resize: "vertical", lineHeight: 1.5 }} data-testid="visit-confirmation-message" />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => gen("VISIT_CONFIRMATION")} disabled={busy}
                  style={{ ...btn("#8E52AB"), flex: 1 }} data-testid="visit-regenerate-confirmation">🔄 Gerar outra</button>
                <button onClick={() => sendConfirmation("whatsapp")} style={{ ...btn("#25D366"), flex: 1 }}
                  data-testid="visit-send-confirmation-whatsapp">WhatsApp</button>
                <button onClick={() => sendConfirmation("email")} style={{ ...btn("#0ea5e9"), flex: 1 }}
                  data-testid="visit-send-confirmation-email">E-mail</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VisitFlowCard;
